import {
  Box,
  CircularProgress,
  TableCell,
  TableRow
} from '@mui/material';
import { Candidate, PollResponse, Precinct, User, Voter, VoterGroup } from '../../types';
import React, { useEffect, useState } from 'react';
import { collection, doc, getDoc, getDocs, query, where } from 'firebase/firestore';

import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import VoterDetails from '../Voters/VoterDetails';
import { db } from '../../firebase';
import useAuth from '../../hooks/useAuth';

interface PrecinctVoterDetailsProps {
  precinct: Precinct;
  position: string;
  candidates: Candidate[];
  voter: Voter;
  index: number;
  expandedRowId: string | null;
  onRowExpand: (voterId: string | null) => void;
}

const PrecinctVoterDetails: React.FC<PrecinctVoterDetailsProps> = ({ 
  precinct, 
  position, 
  candidates, 
  voter, 
  index, 
  expandedRowId,
  onRowExpand 
}) => {
  const { user } = useAuth();
  
  const [votedCandidates, setVotedCandidates] = useState<string[]>([]);
  const [votedCandidateColors, setVotedCandidateColors] = useState<string[]>([]);
  const [lastPoller, setLastPoller] = useState<string>('');
  const [voterGroupLeader, setVoterGroupLeader] = useState<string>('');
  const [isLoading, setIsLoading] = useState(true);
  const [isPollValidated, setIsPollValidated] = useState(false);
  const [partyPreference, setPartyPreference] = useState<string[]>([]);

  const loadVoterGroupLeader = async (voter: Voter) => {
    try {
      // Query for VoterGroups where the voter is a member and level is 'independent' or 'family'
      const voterGroupsRef = collection(db, 'voterGroups');
      const q = query(
        voterGroupsRef, 
        where('members', 'array-contains', { id: voter.id, name: voter.name }),
        where('level', 'in', ['independent', 'family'])
      );

      const querySnapshot = await getDocs(q);
      
      if (!querySnapshot.empty) {
        // Get the first matching VoterGroup
        const firstGroup = querySnapshot.docs[0].data() as VoterGroup;
        return firstGroup.leader?.name || '';
      }
      return '';
    } catch (error) {
      console.error('Error loading voter group leader:', error);
      return '';
    }
  };

  const determineVoterPartyPreference = (pollResponse: PollResponse): string[] => {
    let preference: string[] = [];
    let votedMayor: boolean = false;
    let votedGov: boolean = false;
    let votedViceGov: boolean = false;
    let votedRep: boolean = false;
    
    let lastColor = "";
    for (const [question, response] of Object.entries(pollResponse.responses)) {
      switch (question) {
        case "MAYOR":
          votedMayor = true;
          break;
        case "PROVINCIAL GOVERNOR":
          votedGov = true;
          break;
        case "PROVINCIAL VICE GOVERNOR":
          votedViceGov = true;
          break;
        case "DISTRICT 1 REPRESENTATIVE":
          votedRep = true;
          break;
      }
      
      let votedCandidates: string[] = [];
      if (typeof response === 'string' && candidates.some(c => c.name === response)) {
        votedCandidates = [response];
      } else if (Array.isArray(response)) {
        votedCandidates = response;
      }
      for (const candidate of votedCandidates) {
        const matchedCandidate = candidates.find(c => c.name === candidate);
        if (matchedCandidate) {
          if (lastColor !== matchedCandidate.color && lastColor !== "") {
            preference = ["split"];
            break;
          }
          lastColor = matchedCandidate.color;
        }
      }
    }

    const noVote = !(votedMayor || votedGov || votedViceGov || votedRep);
    if (noVote) {
      preference = ["none"];
    } else {
      const votedPartial = !(votedMayor && votedGov && votedViceGov && votedRep);
      if (votedPartial) {
        preference.push("partial");
      }  
    }

    if (preference.length === 0) {
      preference = ["straight"];
    }

    return preference;
  };

  const loadPrecinctVoterDetails = async (user: User, voter: Voter) => {
    const latestPollResponsePath =
      `tabulationsV2/${user.dashboard}/regions/${precinct.region}/`
      + `provinces/${precinct.province}/districts/${precinct.district}/`
      + `cities/${precinct.city_municipality}/barangays/${precinct.barangay}/`
      + `precincts/${precinct.precinct}/voters/${voter.name}`;
    const latestPollResponseDocRef = doc(db, latestPollResponsePath);
    
    const latestPollResponseDocSnap = await getDoc(latestPollResponseDocRef);
    
    let votedCandidates: string[] = [];
    let votedCandidateColors: string[] = [];
    let lastPoller = '';
    let isPollValidated = false;
    let partyPreference: string[] = [];

    if (latestPollResponseDocSnap.exists()) {
      const latestPoll = latestPollResponseDocSnap.data() as PollResponse;
      const candidateResponse = latestPoll.responses[position];
      
      if (Array.isArray(candidateResponse)) {
        votedCandidates = candidateResponse;
        
        votedCandidateColors = votedCandidates.map(candidate => {
          const matchedCandidate = candidates.find(c => c.name === candidate);
          return matchedCandidate?.color ?? '';
        });
      } else if (typeof candidateResponse === 'string') {
        votedCandidates = [candidateResponse];
        
        const matchedCandidate = candidates.find(c => c.name === candidateResponse);
        votedCandidateColors = [matchedCandidate?.color ?? ''];
      }

      lastPoller = latestPoll.submittedBy;
      
      // Use the validated field from PollResponse
      isPollValidated = latestPoll.validated ?? false;

      partyPreference = determineVoterPartyPreference(latestPoll);
    }
    
    return {
      votedCandidates,
      votedCandidateColors,
      lastPoller,
      isPollValidated,
      partyPreference
    };
  };

  useEffect(() => {
    if (user) {
      Promise.all([
        loadPrecinctVoterDetails(user, voter),
        loadVoterGroupLeader(voter)
      ]).then(([details, leader]) => {
        setVotedCandidates(details.votedCandidates);
        setVotedCandidateColors(details.votedCandidateColors);
        setLastPoller(details.lastPoller);
        setVoterGroupLeader(leader);
        setIsPollValidated(details.isPollValidated);
        setPartyPreference(details.partyPreference);
        setIsLoading(false);
      });      
    }
  }, [user, voter]);

  const handleRowClick = () => {
    onRowExpand(expandedRowId === voter.id ? null : voter.id);
  };

  // Determine text color based on party preference
  const getTextColor = () => {
    if (partyPreference.includes('split')) {
      return '#3f51b5'; // deep indigo blue - highly readable
    } else if (partyPreference.includes('partial')) {
      return '#9c27b0'; // vibrant purple - distinct from common background colors
    }
    return 'inherit'; // default (black)
  };

  return (
    <>
      <TableRow 
        key={voter.id} 
        sx={{ 
          backgroundColor: votedCandidateColors.length > 0
            ? `${votedCandidateColors[0]}33` 
            : 'inherit',
          cursor: 'pointer',
          color: getTextColor(),
          fontWeight: partyPreference.includes('split') || partyPreference.includes('partial') ? 'bold' : 'normal'
        }}
        onClick={handleRowClick}
      >
        <TableCell sx={{ color: 'inherit' }}>{index + 1}</TableCell>
        <TableCell sx={{ color: 'inherit' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            {!isLoading && isPollValidated && (
              <CheckCircleIcon 
                sx={{ 
                  color: 'blue', 
                  fontSize: 20, 
                  verticalAlign: 'middle' 
                }} 
              />
            )}
            {voter.name}
          </Box>
        </TableCell>
        <TableCell sx={{ color: 'inherit' }}>{isLoading ? ( <CircularProgress size={24} /> ) : ( voterGroupLeader )}</TableCell>
        <TableCell sx={{ color: 'inherit' }}>
          {isLoading ? ( 
            <CircularProgress size={24} /> 
          ) : ( 
            <>
              {voter.organizations.map(org => org).join(', ')}
            </>
          )}
        </TableCell>
        <TableCell sx={{ color: 'inherit' }}>{isLoading ? ( <CircularProgress size={24} /> ) : ( partyPreference.join(', ') ?? '' )}</TableCell>
        <TableCell sx={{ color: 'inherit' }}>{isLoading ? ( <CircularProgress size={24} /> ) : ( votedCandidates.join(', ') ?? '' )}</TableCell>
        <TableCell sx={{ color: 'inherit' }}>{isLoading ? ( <CircularProgress size={24} /> ) : ( lastPoller )}</TableCell>
      </TableRow>
      {expandedRowId === voter.id && (
        <TableRow>
          <TableCell colSpan={5}>
            <VoterDetails 
              voter={voter}
              showBackButton={false}
            />
          </TableCell>
        </TableRow>
      )}
    </>
  );
};

export default PrecinctVoterDetails;
